import React, { useState, useRef, useEffect } from 'react';
import {
  IoClose, IoSend, IoSparkles, IoHelpCircle, IoBook,
  IoBulb, IoThumbsUp, IoThumbsDown, IoFlag, IoTime, IoAdd, IoPerson, IoList
} from 'react-icons/io5';
import ReactMarkdown from 'react-markdown';
import DoubtService from '../../_services/DoubtService';
import './student-doubt.css';

const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const StudentDoubtPanel = ({
  isOpen,
  onClose,
  studentId,
  studentName,
  batchId,
  courseId,
  courseName,
  topicId,
  topicName,
  questionId,
  questionText,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentDoubtId, setCurrentDoubtId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [previousDoubts, setPreviousDoubts] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [modalInfo, setModalInfo] = useState(null); // { message, onConfirm? }
  const [showEscalateForm, setShowEscalateForm] = useState(false);
  const [escalationNote, setEscalationNote] = useState('');
  const [unreadTeacherResponses, setUnreadTeacherResponses] = useState([]);
  // Volunteer escalation + consent gating
  const [showConsent, setShowConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  const VOLUNTEER_CONSENT_TEXT =
    'I agree that a trained, verified volunteer mentor can support this student through the platform. All contact stays inside EduSquares and is supervised. Consent can be withdrawn at any time.';
  const messagesEndRef = useRef(null);
  const prevQuestionIdRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      loadDoubtHistory();
    }
  }, [isOpen, studentId]);

  // Reset conversation when opened for a different question
  useEffect(() => {
    if (isOpen && questionId && questionId !== prevQuestionIdRef.current) {
      setMessages([]);
      setCurrentDoubtId(null);
      setShowHistory(false);
      prevQuestionIdRef.current = questionId;
    }
  }, [isOpen, questionId]);

  const getSeenTeacherResponses = () => {
    try {
      return JSON.parse(localStorage.getItem('seen-teacher-responses') || '[]');
    } catch { return []; }
  };

  const markTeacherResponseSeen = (doubtId) => {
    const seen = getSeenTeacherResponses();
    if (!seen.includes(doubtId)) {
      localStorage.setItem('seen-teacher-responses', JSON.stringify([...seen, doubtId]));
    }
    setUnreadTeacherResponses(prev => prev.filter(d => d.id !== doubtId));
  };

  const loadDoubtHistory = async () => {
    setLoadingHistory(true);
    try {
      const doubts = await DoubtService.getStudentDoubts(studentId, {
        limit: 20,
        courseId: courseId
      });
      setPreviousDoubts(doubts);

      // Detect unread teacher responses
      const seen = getSeenTeacherResponses();
      const unread = doubts.filter(d => d.teacherResponse && !seen.includes(d.id));
      setUnreadTeacherResponses(unread);
    } catch (error) {
      console.error('Failed to load doubt history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Context-aware quick prompts
  const getQuickPrompts = () => {
    const prompts = [];
    if (questionText) {
      prompts.push({
        icon: <IoHelpCircle size={16} />,
        text: 'Explain this question',
        prompt: `Can you help me understand this question and how to approach it? The question is: ${stripHtml(questionText).substring(0, 200)}`,
      });
    }
    prompts.push(
      {
        icon: <IoHelpCircle size={16} />,
        text: 'Explain this concept',
        prompt: topicName
          ? `Can you explain ${topicName} in simple terms with examples?`
          : 'Can you explain this concept in simple terms?',
      },
      {
        icon: <IoBook size={16} />,
        text: 'Practice problems',
        prompt: topicName
          ? `Can you give me some practice problems for ${topicName}?`
          : 'Can you give me some practice problems?',
      },
      {
        icon: <IoBulb size={16} />,
        text: 'Study tips',
        prompt: topicName
          ? `What are the best ways to study and remember ${topicName}?`
          : 'What are the best study tips for this topic?',
      },
    );
    return prompts;
  };

  const quickPrompts = getQuickPrompts();

  const handleSendMessage = async (messageContent) => {
    const content = messageContent || input.trim();
    if (!content) return;

    // Add user message
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      // Call AI service with student context
      const context = {
        studentId,
        courseId,
        courseName,
        topicId,
        topicName,
        ...(questionId && { questionId }),
        ...(questionText && { questionText: stripHtml(questionText) }),
      };

      // Format conversation history for API
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp.toISOString ? msg.timestamp.toISOString() : msg.timestamp
      }));

      const aiResult = await DoubtService.askAI(content, context, conversationHistory);

      // Add AI response
      const aiMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: aiResult.response || aiResult,
        timestamp: new Date(),
        metadata: aiResult.metadata,
      };
      setMessages((prev) => [...prev, aiMessage]);

      // Persist doubt record (isolated try/catch so DB failures don't affect chat)
      try {
        if (messages.length === 0) {
          const doubt = await DoubtService.createDoubt({
            studentId,
            studentName,
            batchId,
            courseId,
            courseName,
            topicId,
            topicName,
            question: content,
            aiResponse: aiResult.response || aiResult,
            status: 'answered',
            conversationHistory: [userMessage, aiMessage],
            aiModel: aiResult.metadata?.model,
            aiProcessingTimeMs: aiResult.metadata?.processingTimeMs,
            aiConfidence: aiResult.metadata?.confidence,
            ...(questionId && {
              contextData: {
                questionId,
                ...(questionText && { questionText: stripHtml(questionText) }),
                source: 'playground',
              },
            }),
          });
          setCurrentDoubtId(doubt.data?.id || doubt.id);
        } else if (currentDoubtId) {
          await DoubtService.updateDoubt(currentDoubtId, {
            conversationHistory: [...messages, userMessage, aiMessage]
          });
        }
      } catch (dbError) {
        console.error('Failed to persist doubt record:', dbError);
      }
    } catch (error) {
      console.error('AI Chat error:', error);

      let errorText;
      if (error.status === 429 || error.errorCode === 'AI_QUOTA_EXCEEDED') {
        errorText = error.message || "You've reached your daily AI usage limit. Please try again tomorrow, or ask your teacher directly.";
      } else {
        errorText = `**Error**: ${error.message || 'Failed to get AI response'}\n\nPlease try again or contact your teacher for help.`;
      }

      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: errorText,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleFeedback = async (helpful) => {
    try {
      const doubtId = await ensureDoubtRecord();
      if (!doubtId) return;

      await DoubtService.submitFeedback(
        doubtId,
        helpful ? 'helpful' : 'not_helpful'
      );

      setModalInfo({
        message: helpful
          ? 'Thanks for your feedback!'
          : "Sorry this wasn't helpful. You can escalate this to your teacher."
      });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  // Helper: ensure a doubt record exists in DB, creating one if needed
  const ensureDoubtRecord = async () => {
    if (currentDoubtId) return currentDoubtId;
    if (messages.length === 0) return null;

    try {
      const firstUserMsg = messages.find(m => m.role === 'user');
      const firstAiMsg = messages.find(m => m.role === 'assistant');
      const doubt = await DoubtService.createDoubt({
        studentId,
        studentName,
        batchId,
        courseId,
        courseName,
        topicId,
        topicName,
        question: firstUserMsg?.content || '',
        aiResponse: firstAiMsg?.content || '',
        status: 'answered',
        conversationHistory: messages,
        ...(questionId && {
          contextData: {
            questionId,
            ...(questionText && { questionText: stripHtml(questionText) }),
            source: 'playground',
          },
        }),
      });
      const id = doubt.data?.id || doubt.id;
      setCurrentDoubtId(id);
      return id;
    } catch (error) {
      console.error('Failed to create doubt record:', error);
      return null;
    }
  };

  const handleEscalate = () => {
    if (messages.length === 0) {
      setModalInfo({ message: 'Please start a conversation first before escalating.' });
      return;
    }
    setShowEscalateForm(true);
    setEscalationNote('');
  };

  const submitEscalation = async (target = 'teacher') => {
    // Volunteer path is consent-gated: no consent on file → show the prompt.
    if (target === 'volunteer') {
      try {
        const status = await DoubtService.getVolunteerConsent(studentId);
        if (!status?.consented) {
          setShowConsent(true);
          return;
        }
      } catch (error) {
        console.error('Failed to check consent:', error);
        setModalInfo({ message: 'Could not check consent status. Please try again.' });
        return;
      }
    }

    try {
      const doubtId = await ensureDoubtRecord();
      if (!doubtId) {
        setModalInfo({ message: 'Failed to save your doubt. Please try again.' });
        return;
      }
      await DoubtService.escalateDoubt(doubtId, null, escalationNote.trim() || undefined, target);
      setShowEscalateForm(false);
      setEscalationNote('');
      setModalInfo({
        message:
          target === 'volunteer'
            ? 'Your doubt has been sent to a volunteer mentor. Someone will pick it up soon!'
            : 'Your doubt has been sent to your teacher. They will reach out to help you soon!'
      });
    } catch (error) {
      console.error('Failed to escalate doubt:', error);
      setModalInfo({ message: error.message || 'Failed to escalate doubt. Please try again.' });
    }
  };

  const grantConsentAndEscalate = async () => {
    if (!consentChecked) return;
    try {
      await DoubtService.grantVolunteerConsent({
        studentId,
        method: 'ilearn_student',
        grantedByRelation: 'self',
        consentText: VOLUNTEER_CONSENT_TEXT
      });
      setShowConsent(false);
      setConsentChecked(false);
      await submitEscalation('volunteer');
    } catch (error) {
      console.error('Failed to record consent:', error);
      setModalInfo({ message: 'Could not record consent. Please try again.' });
    }
  };

  const handleQuickPrompt = (prompt) => {
    handleSendMessage(prompt);
  };

  const loadPreviousDoubt = (doubt) => {
    setShowHistory(false);
    setShowEscalateForm(false);
    setCurrentDoubtId(doubt.id);
    if (doubt.teacherResponse) {
      markTeacherResponseSeen(doubt.id);
    }
    let msgs;
    if (doubt.conversationHistory && doubt.conversationHistory.length > 0) {
      msgs = [...doubt.conversationHistory];
    } else {
      // Create conversation from question and response
      msgs = [
        {
          id: '1',
          role: 'user',
          content: doubt.question,
          timestamp: doubt.createdAt
        }
      ];
      if (doubt.aiResponse) {
        msgs.push({
          id: '2',
          role: 'assistant',
          content: doubt.aiResponse,
          timestamp: doubt.createdAt
        });
      }
    }
    // Append teacher response if the doubt was resolved by a teacher
    if (doubt.teacherResponse) {
      msgs.push({
        id: 'teacher-response',
        role: 'teacher',
        content: doubt.teacherResponse,
        timestamp: doubt.resolvedAt || doubt.updatedAt
      });
    }
    setMessages(msgs);
  };

  const startNewConversation = () => {
    if (messages.length > 0) {
      setModalInfo({
        message: 'Start a new conversation? Your current conversation has been saved.',
        onConfirm: () => {
          setMessages([]);
          setCurrentDoubtId(null);
          setShowHistory(false);
        }
      });
    } else {
      setMessages([]);
      setCurrentDoubtId(null);
      setShowHistory(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="student-doubt-overlay" onClick={onClose}></div>
      <div className="student-doubt-panel">
        {/* Header */}
        <div className="student-doubt-header">
          <div className="student-doubt-header-content">
            <IoSparkles size={20} className="student-doubt-header-icon" />
            <div>
              <h3>Ask Your Doubt</h3>
              <p>{topicName || courseName || 'Get help with your studies'}</p>
            </div>
          </div>
          <div className="student-doubt-header-actions">
            <button
              className="student-doubt-new-btn"
              onClick={startNewConversation}
              aria-label="New conversation"
              title="Start new conversation"
            >
              <IoAdd size={20} />
            </button>
            <button
              className="student-doubt-history-btn"
              onClick={() => setShowHistory(!showHistory)}
              aria-label="View history"
              title="View previous doubts"
            >
              <IoTime size={20} />
            </button>
            <button
              className="student-doubt-viewall-btn"
              onClick={() => { window.location.href = '/my-doubts'; }}
              aria-label="View all doubts"
              title="View all doubts"
            >
              <IoList size={20} />
            </button>
            <button className="student-doubt-close" onClick={onClose} aria-label="Close">
              <IoClose size={20} />
            </button>
          </div>
        </div>

        {/* History Sidebar */}
        {showHistory && (
          <div className="student-doubt-history">
            <h4>Previous Doubts</h4>
            {loadingHistory ? (
              <div className="student-doubt-history-loading">
                <div className="spinning-loader"></div>
                <p>Loading history...</p>
              </div>
            ) : previousDoubts.length === 0 ? (
              <p className="student-doubt-history-empty">No previous doubts yet</p>
            ) : (
              <div className="student-doubt-history-list">
                {previousDoubts.map((doubt) => (
                  <div
                    key={doubt.id}
                    className="student-doubt-history-item"
                    onClick={() => loadPreviousDoubt(doubt)}
                  >
                    <div className="student-doubt-history-item-question">
                      {doubt.question.substring(0, 80)}
                      {doubt.question.length > 80 ? '...' : ''}
                    </div>
                    <div className="student-doubt-history-item-meta">
                      <span className={`status status-${doubt.status}`}>
                        {doubt.status}
                      </span>
                      <span className="date">
                        {new Date(doubt.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="student-doubt-messages">
          {/* Teacher response notifications */}
          {unreadTeacherResponses.length > 0 && messages.length === 0 && (
            <div className="student-doubt-teacher-notifications">
              {unreadTeacherResponses.map(doubt => (
                <div
                  key={doubt.id}
                  className="student-doubt-teacher-notification"
                  onClick={() => loadPreviousDoubt(doubt)}
                >
                  <div className="student-doubt-teacher-notification-icon">
                    <IoPerson size={16} />
                  </div>
                  <div className="student-doubt-teacher-notification-text">
                    <strong>Your teacher responded</strong>
                    <span>{doubt.topicName || doubt.courseName || 'your doubt'}</span>
                  </div>
                  <span className="student-doubt-teacher-notification-action">View</span>
                </div>
              ))}
            </div>
          )}

          {messages.length === 0 && (
            <div className="student-doubt-welcome">
              <IoSparkles size={48} className="student-doubt-welcome-icon" />
              <h4>Hi {studentName.split(' ')[0]}!</h4>
              {questionText ? (
                <>
                  <p>I see you have a doubt about this question:</p>
                  <div
                    className="student-doubt-question-context"
                    dangerouslySetInnerHTML={{ __html: questionText }}
                  />
                  <p>Type your specific doubt below, or use a quick action:</p>
                </>
              ) : (
                <>
                  <p>I'm here to help clear your doubts. Ask me anything about your studies!</p>
                  <div className="student-doubt-tips">
                    <p><strong>Tips for better answers:</strong></p>
                    <ul>
                      <li>Be specific about what you don't understand</li>
                      <li>Ask for examples if you need them</li>
                      <li>Let me know if you want a simpler explanation</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          )}

          {messages.map((message) => (
            <div
              key={message.id}
              className={`student-doubt-message ${
                message.role === 'user'
                  ? 'student-doubt-message-user'
                  : message.role === 'teacher'
                  ? 'student-doubt-message-teacher'
                  : 'student-doubt-message-assistant'
              }`}
            >
              {message.role === 'assistant' && (
                <div className="student-doubt-message-avatar">
                  <IoSparkles size={16} />
                </div>
              )}
              {message.role === 'teacher' && (
                <div className="student-doubt-message-avatar student-doubt-teacher-avatar">
                  <IoPerson size={16} />
                </div>
              )}
              <div className="student-doubt-message-content">
                {message.role === 'teacher' && (
                  <div className="student-doubt-teacher-label">Teacher Response</div>
                )}
                {message.role === 'assistant' || message.role === 'teacher' ? (
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                ) : (
                  message.content
                )}
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="student-doubt-message student-doubt-message-assistant">
              <div className="student-doubt-message-avatar">
                <IoSparkles size={16} />
              </div>
              <div className="student-doubt-typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Escalation form */}
        {showEscalateForm && (
          <div className="student-doubt-escalate-form">
            <div className="student-doubt-escalate-form-header">
              <IoFlag size={16} />
              <strong>Send to your teacher</strong>
            </div>
            <textarea
              className="student-doubt-escalate-textarea"
              placeholder="What specifically do you need help with? (optional)"
              value={escalationNote}
              onChange={(e) => setEscalationNote(e.target.value)}
              rows={3}
            />
            <div className="student-doubt-escalate-form-actions">
              <button
                className="student-doubt-escalate-cancel"
                onClick={() => setShowEscalateForm(false)}
              >
                Cancel
              </button>
              <button
                className="student-doubt-escalate-submit"
                onClick={() => submitEscalation('teacher')}
              >
                <IoSend size={14} /> Send to Teacher
              </button>
              <button
                className="student-doubt-escalate-submit"
                onClick={() => submitEscalation('volunteer')}
              >
                <IoSend size={14} /> Ask a Volunteer Mentor
              </button>
            </div>
          </div>
        )}

        {/* Volunteer consent prompt */}
        {showConsent && (
          <div className="student-doubt-escalate-form">
            <div className="student-doubt-escalate-form-header">
              <IoFlag size={16} />
              <strong>One quick permission</strong>
            </div>
            <p style={{ fontSize: 13, lineHeight: 1.5, margin: '8px 0' }}>
              {VOLUNTEER_CONSENT_TEXT}
            </p>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
              <span>I agree to the above and want to connect with a volunteer mentor.</span>
            </label>
            <div className="student-doubt-escalate-form-actions">
              <button
                className="student-doubt-escalate-cancel"
                onClick={() => { setShowConsent(false); setConsentChecked(false); }}
              >
                Cancel
              </button>
              <button
                className="student-doubt-escalate-submit"
                onClick={grantConsentAndEscalate}
                disabled={!consentChecked}
              >
                <IoSend size={14} /> Agree &amp; Continue
              </button>
            </div>
          </div>
        )}

        {/* Feedback Actions (shown after AI response) */}
        {messages.length > 0 && messages[messages.length - 1]?.role === 'assistant' && !isTyping && !showEscalateForm && (
          <div className="student-doubt-feedback">
            <p>Was this answer helpful?</p>
            <div className="student-doubt-feedback-actions">
              <button
                className="btn-feedback btn-helpful"
                onClick={() => handleFeedback(true)}
              >
                <IoThumbsUp size={16} /> Yes
              </button>
              <button
                className="btn-feedback btn-not-helpful"
                onClick={() => handleFeedback(false)}
              >
                <IoThumbsDown size={16} /> No
              </button>
              <button
                className="btn-feedback btn-escalate"
                onClick={handleEscalate}
              >
                <IoFlag size={16} /> Ask Teacher
              </button>
            </div>
          </div>
        )}

        {/* Quick Prompts */}
        {messages.length === 0 && (
          <div className="student-doubt-quick-prompts">
            <p className="student-doubt-quick-prompts-title">Quick actions:</p>
            <div className="student-doubt-quick-prompts-grid">
              {quickPrompts.map((prompt, index) => (
                <button
                  key={index}
                  className="student-doubt-quick-prompt"
                  onClick={() => handleQuickPrompt(prompt.prompt)}
                >
                  {prompt.icon}
                  <span>{prompt.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="student-doubt-input-container">
          <input
            type="text"
            className="student-doubt-input"
            placeholder="Type your doubt here..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            disabled={isTyping}
          />
          <button
            className="student-doubt-send"
            onClick={() => handleSendMessage()}
            disabled={!input.trim() || isTyping}
            aria-label="Send message"
          >
            <IoSend size={20} />
          </button>
        </div>
      </div>

      {/* Confirmation / Info Modal */}
      {modalInfo && (
        <div className="student-doubt-modal-overlay" onClick={() => setModalInfo(null)}>
          <div className="student-doubt-modal" onClick={(e) => e.stopPropagation()}>
            <p>{modalInfo.message}</p>
            <div className="student-doubt-modal-actions">
              {modalInfo.onConfirm ? (
                <>
                  <button
                    className="student-doubt-modal-btn student-doubt-modal-btn-cancel"
                    onClick={() => setModalInfo(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="student-doubt-modal-btn student-doubt-modal-btn-confirm"
                    onClick={() => { modalInfo.onConfirm(); setModalInfo(null); }}
                  >
                    Confirm
                  </button>
                </>
              ) : (
                <button
                  className="student-doubt-modal-btn student-doubt-modal-btn-confirm"
                  onClick={() => setModalInfo(null)}
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StudentDoubtPanel;
